import React from 'react';
import { Spin } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useMatchMedia } from '@hooks/useMatchMedia';
import { useProjectIssuesAndUpgrades, ISSUE_UPGRADE_COL } from '@hooks/projects/useProjectIssuesAndUpgrades';
import {
    CLIENT_TICKET_COL,
    useProjectClientTickets,
} from '@hooks/projects/useProjectClientTickets';
import { useAuthStore } from '@stores/auth.store';
import IssuesUpgradesKanbanBoard from '@/components/module/projects/issues_upgrades_tab/IssuesUpgradesKanbanBoard';
import IssueUpgradeCreateTaskModal from '@/components/module/projects/issues_upgrades_tab/IssueUpgradeCreateTaskModal';
import IssueUpgradeCreateModal from '@/components/module/projects/issues_upgrades_tab/IssueUpgradeCreateModal';
import IssueUpgradeSubmitModal from '@/components/module/projects/issues_upgrades_tab/IssueUpgradeSubmitModal';
import ClientTicketSubmitModal from '@/components/module/projects/issues_upgrades_tab/ClientTicketSubmitModal';
import ClientTicketDetailModal from '@/components/module/projects/client_tickets_tab/ClientTicketDetailModal';
import IssueUpgradeDetailModal from '@/components/module/projects/issues_upgrades_tab/IssueUpgradeDetailModal';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    BoardKanbanBoardShell,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
    BoardPillTabRow,
    BoardPillTab,
    BoardPillTabLabel,
} from '../style';
export default function ProjectIssuesAndUpgradesTab({ projectId }) {
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [selectedBoard, setSelectedBoard] = React.useState('in_house');
    const [clientDropTarget, setClientDropTarget] = React.useState(null);
    const [clientDraggingColumn, setClientDraggingColumn] = React.useState(null);
    const [inHouseSubmitModalOpen, setInHouseSubmitModalOpen] = React.useState(false);
    const [pendingInHouseSubmitId, setPendingInHouseSubmitId] = React.useState(null);
    const [clientSubmitModalOpen, setClientSubmitModalOpen] = React.useState(false);
    const [pendingClientSubmitItem, setPendingClientSubmitItem] = React.useState(null);
    const [inHousePage, setInHousePage] = React.useState(1);
    const [clientPage, setClientPage] = React.useState(1);

    React.useEffect(() => {
        setInHousePage(1);
        setClientPage(1);
    }, [projectId]);

    const user = useAuthStore((s) => s.user);
    const useFixedClientColumns = useMatchMedia('(max-width: 1680px)');

    const {
        activeRows,
        submittedRows,
        resolvedRows,
        isLoading,
        isFetching,
        canCreate,
        canView,
        canDrag,
        canSubmit,
        canResolve,
        canDelete,
        canUpdate,
        createModal,
        deletePending,
        promptDeleteIssueOrUpgrade,
        openCreateTaskFromIssue,
        canCreateTaskFromIssue,
        submitIssueOrUpgrade,
        submitPending,
        clearDropTarget,
        dropTarget,
        draggingColumn,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        refetch: refetchInHouseIssues,
        createTaskFromIssueModalProps,
        total: inHouseServerTotal,
    } = useProjectIssuesAndUpgrades(projectId, { page: inHousePage });
    const {
        activeRows: clientActiveRows,
        submittedRows: clientSubmittedRows,
        submittedToClientRows,
        acknowledgedRows,
        isLoading: clientLoading,
        isFetching: clientFetching,
        canView: canClientView,
        canSubmit: canClientSubmit,
        canSubmitToClient,
        canDrag: canClientDrag,
        movePending: clientMovePending,
        moveToSubmitted,
        moveToSubmittedToClient,
        refetch: refetchClientTickets,
        total: clientServerTotal,
    } = useProjectClientTickets(projectId, { page: clientPage });

    const isInHouseBoard = selectedBoard === 'in_house';
    const selectedActiveRows = isInHouseBoard ? activeRows : clientActiveRows;
    const selectedSubmittedRows = isInHouseBoard ? submittedRows : clientSubmittedRows;
    // Client Tickets board: 3rd column is "Submitted to Client", 4th is "Acknowledged".
    const selectedResolvedRows = isInHouseBoard ? resolvedRows : submittedToClientRows;
    const selectedSubmittedToClientRows = isInHouseBoard ? undefined : acknowledgedRows;
    const inHouseTotal = (activeRows?.length || 0) + (submittedRows?.length || 0) + (resolvedRows?.length || 0);
    const clientSideTotal =
        (clientActiveRows?.length || 0) +
        (clientSubmittedRows?.length || 0) +
        (submittedToClientRows?.length || 0) +
        (acknowledgedRows?.length || 0);
    const inHouseHasMore = inHouseTotal < inHouseServerTotal;
    const clientHasMore = clientSideTotal < clientServerTotal;

    React.useEffect(() => {
        if (isInHouseBoard || typeof document === 'undefined') return undefined;
        const html = document.documentElement;
        const body = document.body;
        const prevHtmlOverscrollX = html.style.overscrollBehaviorX;
        const prevBodyOverscrollX = body.style.overscrollBehaviorX;
        html.style.overscrollBehaviorX = 'none';
        body.style.overscrollBehaviorX = 'none';
        return () => {
            html.style.overscrollBehaviorX = prevHtmlOverscrollX;
            body.style.overscrollBehaviorX = prevBodyOverscrollX;
        };
    }, [isInHouseBoard]);

    React.useEffect(() => {
        if (isInHouseBoard) {
            refetchInHouseIssues?.();
            return;
        }
        refetchClientTickets?.();
    }, [isInHouseBoard, refetchInHouseIssues, refetchClientTickets]);

    if (isInHouseBoard ? !canView : !canClientView) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <SettingOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>Issues &amp; Upgrades</EmptyStateTitle>
                <EmptyStateDescription>You do not have permission to view this tab.</EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    const mainContent = (
        <BoardTabRoot
            style={{
                marginBottom: 0,
                marginRight: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                '--board-kanban-inline-gutter': 'clamp(16px, 4vw, 40px)',
            }}
        >
            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="flex-start"
                $gap="16px"
                $flexWrap="wrap"
                style={{ marginBottom: 24 }}
            >
                <div>
                    <BoardPageTitle>Issues &amp; Upgrades</BoardPageTitle>
                    <BoardPageSubtitle>
                        Track active items, submissions, and resolved outcomes for this project.
                    </BoardPageSubtitle>
                </div>
            </Container>
            <Container
                $display="flex"
                $alignItems="center"
                $justifyContent="space-between"
                $gap="12px"
                $flexWrap="nowrap"
                style={{ marginBottom: 16, flexShrink: 0, paddingRight: 16 }}
            >
                <BoardPillTabRow style={{ marginBottom: 0, flexShrink: 0 }}>
                    <BoardPillTab
                        type="button"
                        $active={isInHouseBoard}
                        onClick={() => {
                            setSelectedBoard('in_house');
                            setSelectedItem(null);
                        }}
                        style={{ minWidth: 150, justifyContent: 'center' }}
                    >
                        <BoardPillTabLabel
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}
                        >
                            <span>In House</span>
                            <span
                                style={{
                                    minWidth: 22,
                                    height: 22,
                                    borderRadius: 999,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 6px',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: isInHouseBoard
                                        ? 'var(--ant-color-primary, #7c3aed)'
                                        : 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
                                    background: isInHouseBoard
                                        ? 'var(--ant-color-primary-bg, rgba(124,58,237,0.14))'
                                        : 'var(--ant-color-fill-secondary, rgba(0,0,0,0.06))',
                                    border: isInHouseBoard
                                        ? '1px solid color-mix(in srgb, var(--ant-color-primary, #7c3aed) 35%, transparent)'
                                        : '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.12))',
                                }}
                            >
                                {inHouseTotal}
                            </span>
                        </BoardPillTabLabel>
                    </BoardPillTab>
                    <BoardPillTab
                        type="button"
                        $active={!isInHouseBoard}
                        onClick={() => {
                            setSelectedBoard('client_side');
                            setSelectedItem(null);
                        }}
                        style={{ minWidth: 150, justifyContent: 'center' }}
                    >
                        <BoardPillTabLabel
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}
                        >
                            <span>Client Tickets</span>
                            <span
                                style={{
                                    minWidth: 22,
                                    height: 22,
                                    borderRadius: 999,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 6px',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: !isInHouseBoard
                                        ? 'var(--ant-color-primary, #7c3aed)'
                                        : 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
                                    background: !isInHouseBoard
                                        ? 'var(--ant-color-primary-bg, rgba(124,58,237,0.14))'
                                        : 'var(--ant-color-fill-secondary, rgba(0,0,0,0.06))',
                                    border: !isInHouseBoard
                                        ? '1px solid color-mix(in srgb, var(--ant-color-primary, #7c3aed) 35%, transparent)'
                                        : '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.12))',
                                }}
                            >
                                {clientSideTotal}
                            </span>
                        </BoardPillTabLabel>
                    </BoardPillTab>
                </BoardPillTabRow>
                {isInHouseBoard && canCreate ? (
                    <Button
                        type="button"
                        $primary
                        $blackOnLight
                        $size="13px"
                        $padding="6px 14px"
                        icon={<PlusOutlined />}
                        onClick={createModal.openModal}
                    >
                        Create
                    </Button>
                ) : null}
            </Container>

            <BoardKanbanBoardShell
                style={
                    isInHouseBoard
                        ? undefined
                        : useFixedClientColumns
                            ? {
                                marginLeft: -24,
                                width: 'calc(100% + 24px)',
                            }
                            : {
                                paddingRight: 24,
                            }
                }
            >
                <Container
                    $display="flex"
                    $flexDirection="column"
                    $flex="1"
                    $minHeight="0"
                    $overflow="hidden"
                    style={{ width: '100%' }}
                >
                    <IssuesUpgradesKanbanBoard
                        activeRows={selectedActiveRows}
                        submittedRows={selectedSubmittedRows}
                        resolvedRows={selectedResolvedRows}
                        extraRows={selectedSubmittedToClientRows}
                        boardKind={isInHouseBoard ? 'issue_upgrade' : 'client_ticket'}
                        hasMore={isInHouseBoard ? inHouseHasMore : clientHasMore}
                        isLoadingMore={isInHouseBoard ? (isFetching && !isLoading) : (clientFetching && !clientLoading)}
                        onLoadMore={
                            isInHouseBoard
                                ? () => { if (inHouseHasMore) setInHousePage((prev) => prev + 1); }
                                : () => { if (clientHasMore) setClientPage((prev) => prev + 1); }
                        }
                        resolvedColumnId={isInHouseBoard ? undefined : CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT}
                        canDrag={isInHouseBoard ? canDrag : canClientDrag}
                        dropTarget={isInHouseBoard ? dropTarget : clientDropTarget}
                        draggingColumn={isInHouseBoard ? draggingColumn : clientDraggingColumn}
                        onDndDragStart={
                            isInHouseBoard
                                ? handleDndDragStart
                                : (payload) => setClientDraggingColumn(payload?.sourceColumn ?? null)
                        }
                        onDndDragOverColumn={
                            isInHouseBoard
                                ? handleDndDragOverColumn
                                : (columnId) => setClientDropTarget(columnId ?? null)
                        }
                        onDndDragEnd={
                            isInHouseBoard
                                ? ({ overColumnId, payload }) => {
                                    const source = payload?.sourceColumn ?? null;
                                    const target = overColumnId ?? null;
                                    if (source === ISSUE_UPGRADE_COL.ACTIVE && target === ISSUE_UPGRADE_COL.SUBMITTED) {
                                        if (!canSubmit) return;
                                        const submitId = payload?.id ?? null;
                                        if (!submitId) return;
                                        setPendingInHouseSubmitId(submitId);
                                        setInHouseSubmitModalOpen(true);
                                        clearDropTarget?.();
                                        return;
                                    }
                                    handleDndDragEnd?.({ overColumnId, payload });
                                }
                                : ({ overColumnId, payload }) => {
                                    setClientDropTarget(null);
                                    setClientDraggingColumn(null);
                                    const source = String(payload?.sourceColumn ?? '');
                                    const target = String(overColumnId ?? '');
                                    const movedItem = payload?.item ?? { id: payload?.id };
                                    if (!source || !target || source === target || !movedItem?.id) return;
                                    if (source === CLIENT_TICKET_COL.ACTIVE && target === CLIENT_TICKET_COL.SUBMITTED) {
                                        if (!canClientSubmit) return;
                                        setPendingClientSubmitItem(movedItem);
                                        setClientSubmitModalOpen(true);
                                        return;
                                    }
                                    if (
                                        source === CLIENT_TICKET_COL.ACTIVE &&
                                        target === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT
                                    ) {
                                        if (!canSubmitToClient) return;
                                        moveToSubmittedToClient(movedItem);
                                        return;
                                    }
                                    if (
                                        source === CLIENT_TICKET_COL.SUBMITTED &&
                                        target === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT
                                    ) {
                                        if (!canSubmitToClient) return;
                                        moveToSubmittedToClient(movedItem);
                                    }
                                }
                        }
                        onAdd={undefined}
                        onSelectItem={setSelectedItem}
                        canSubmit={isInHouseBoard ? canSubmit : canClientSubmit}
                        canResolve={isInHouseBoard ? canResolve : canSubmitToClient}
                        canUpdate={isInHouseBoard ? canUpdate : false}
                        canDelete={isInHouseBoard ? canDelete : false}
                        currentUserId={user?.id}
                        onEditItem={(row) => createModal.openEditModal(row)}
                        onDeleteItem={(row) =>
                            promptDeleteIssueOrUpgrade(row, {
                                onSuccess: () =>
                                    setSelectedItem((cur) =>
                                        cur && String(cur?.id ?? '') === String(row?.id ?? '')
                                            ? null
                                            : cur,
                                    ),
                            })
                        }
                        canCreateProjectTask={isInHouseBoard ? canCreateTaskFromIssue : false}
                        onOpenCreateProjectTask={
                            isInHouseBoard && typeof openCreateTaskFromIssue === 'function'
                                ? (row) => openCreateTaskFromIssue(row)
                                : undefined
                        }
                        deletePending={isInHouseBoard ? deletePending : clientMovePending}
                        boardTitle={isInHouseBoard ? 'Issues & Upgrades' : 'Client Tickets'}
                        activeLabel="Active"
                        submittedLabel="Submitted"
                        resolvedLabel={isInHouseBoard ? 'Resolve' : 'Submitted to Client'}
                        extraLabel={isInHouseBoard ? '' : 'Acknowledged'}
                        showHeader={false}
                        columnMode={isInHouseBoard ? 'adaptive' : useFixedClientColumns ? 'fixed' : 'adaptive'}
                        extraColumnId={CLIENT_TICKET_COL.ACKNOWLEDGED}
                        nonDraggableColumnIds={
                            isInHouseBoard
                                ? []
                                : [CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT, CLIENT_TICKET_COL.ACKNOWLEDGED]
                        }
                        canDropRule={
                            isInHouseBoard
                                ? undefined
                                : (sourceColumn, targetColumn) => {
                                    if (
                                        sourceColumn === CLIENT_TICKET_COL.ACTIVE &&
                                        targetColumn === CLIENT_TICKET_COL.SUBMITTED
                                    ) {
                                        return canClientSubmit;
                                    }
                                    if (
                                        sourceColumn === CLIENT_TICKET_COL.ACTIVE &&
                                        targetColumn === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT
                                    ) {
                                        return canSubmitToClient;
                                    }
                                    if (
                                        sourceColumn === CLIENT_TICKET_COL.SUBMITTED &&
                                        targetColumn === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT
                                    ) {
                                        return canSubmitToClient;
                                    }
                                    return false;
                                }
                        }
                        getDropBlockedMessage={
                            isInHouseBoard
                                ? undefined
                                : (source, target) => {
                                    const s = String(source);
                                    const t = String(target);
                                    if (
                                        s === CLIENT_TICKET_COL.ACTIVE &&
                                        t === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT &&
                                        !canSubmitToClient
                                    ) {
                                        return "You don't have permission to submit this ticket to the client.";
                                    }
                                    if (s === CLIENT_TICKET_COL.ACTIVE && t === CLIENT_TICKET_COL.ACKNOWLEDGED) {
                                        return 'Use each stage in order: Submitted → Submitted to Client, then Acknowledged.';
                                    }
                                    if (
                                        s === CLIENT_TICKET_COL.SUBMITTED &&
                                        t === CLIENT_TICKET_COL.ACKNOWLEDGED
                                    ) {
                                        return 'Send the ticket to the client before moving it to Acknowledged.';
                                    }
                                    if (
                                        s === CLIENT_TICKET_COL.SUBMITTED &&
                                        t === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT &&
                                        !canSubmitToClient
                                    ) {
                                        return "You don't have permission to submit this ticket to the client.";
                                    }
                                    return null;
                                }
                        }
                    />
                </Container>
            </BoardKanbanBoardShell>
        </BoardTabRoot>
    );

    return (
        <>
            {isLoading || (!isInHouseBoard && clientLoading) ? (
                <Container
                    style={{
                        margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <EmptyStateBlock>
                        <DetailPanelLoading>
                            <Spin size="large" />
                        </DetailPanelLoading>
                    </EmptyStateBlock>
                </Container>
            ) : (
                <Container $display="flex" $flex="1" $minHeight="0" $overflow="hidden">
                    {mainContent}
                </Container>
            )}
            <IssueUpgradeCreateTaskModal {...createTaskFromIssueModalProps} />
            <IssueUpgradeCreateModal
                form={createModal.form}
                open={createModal.modalOpen}
                onCancel={createModal.closeModal}
                onSubmit={createModal.handleSubmit}
                submitPending={createModal.createPending}
                pendingAttachments={createModal.pendingAttachments}
                pendingLinks={createModal.pendingLinks}
                existingAttachments={createModal.existingAttachments}
                handleFilesSelected={createModal.handleFilesSelected}
                removeAttachment={createModal.removeAttachment}
                handleLinkRequest={createModal.handleLinkRequest}
                handleEditorLinkRemoved={createModal.handleEditorLinkRemoved}
                handleLinkModalSubmit={createModal.handleLinkModalSubmit}
                linkModalOpen={createModal.linkModalOpen}
                setLinkModalOpen={createModal.setLinkModalOpen}
                linkModalForm={createModal.linkModalForm}
                linkModalCallbackRef={createModal.linkModalCallbackRef}
                mentionUsers={createModal.mentionUsers}
                mode={createModal.editingItem ? 'edit' : 'create'}
            />
            {isInHouseBoard ? (
                <IssueUpgradeDetailModal
                    projectId={projectId}
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                />
            ) : (
                <ClientTicketDetailModal item={selectedItem} kind="client_ticket" onClose={() => setSelectedItem(null)} />
            )}
            <ClientTicketSubmitModal
                open={clientSubmitModalOpen}
                loading={clientMovePending}
                onCancel={() => {
                    if (clientMovePending) return;
                    setClientSubmitModalOpen(false);
                    setPendingClientSubmitItem(null);
                }}
                onSubmit={({ state, reason, files, attachments }) => {
                    if (!pendingClientSubmitItem) return;
                    moveToSubmitted(
                        pendingClientSubmitItem,
                        {
                            state: state === 'improper' ? 'improper' : 'genuine',
                            reason: String(reason ?? '').trim() || undefined,
                            files,
                            attachments,
                        },
                        {
                            onSuccess: () => {
                                setClientSubmitModalOpen(false);
                                setPendingClientSubmitItem(null);
                            },
                            onError: () => {
                                // Keep modal open so user can adjust and retry.
                            },
                        },
                    );
                }}
            />
            <IssueUpgradeSubmitModal
                open={inHouseSubmitModalOpen}
                loading={submitPending}
                onCancel={() => {
                    if (submitPending) return;
                    setInHouseSubmitModalOpen(false);
                    setPendingInHouseSubmitId(null);
                }}
                onSubmit={({ state, reason, files, attachments }) => {
                    if (!pendingInHouseSubmitId) return;
                    submitIssueOrUpgrade(
                        pendingInHouseSubmitId,
                        {
                            state: state === 'improper' ? 'improper' : 'genuine',
                            reason: String(reason ?? '').trim() || undefined,
                            files,
                            attachments,
                        },
                        {
                            onSuccess: () => {
                                setInHouseSubmitModalOpen(false);
                                setPendingInHouseSubmitId(null);
                            },
                            onError: () => {
                                // Keep modal open so user can adjust and retry.
                            },
                        },
                    );
                }}
            />
        </>
    );
}
